import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, PlusIcon, PriceTagIcon, XIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import NoteBox from '@repo/ui/note-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getOptionProductDatePrice } from '../../../api/option-prices/get-option-price-by-details'
import { getProductDateOptions } from '../../../api/products/get-product-date-options'
import { OptionPartial } from '../../../lib/types/models/options/option'
import { JourneyInput, JourneyOptionInput } from '../../../lib/types/models/product/journey-input'
import { BaseFormProps, IdInput } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getNoThemeAvailableMessage } from '../../../lib/utils/functions/product-date.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import InformationToolTip from '../../elements/information-tooltip'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<JourneyInput, true> & {
  productDateId: number
  periodName?: string
  className?: string
  // options already saved on the séjour, labelled even when no longer proposed for the date
  savedOptions?: OptionPartial[]
}

const formSchema: ZodType<JourneyInput> = z
  .object({
    mainOptionId: createNumberSchema('option', {
      isPositive: true,
      errorMessage: 'Veuillez saisir une option valide'
    }),
    mainOptionPrice: createNumberSchema('prix', { nonNegative: true }),
    isMandatoryUnderAge: z.boolean().optional(),
    mandatoryUnderAge: createNumberSchema('âge minimum', { optional: true }),
    fa1eIncluded: z.boolean().optional(),
    fa1ePrice: createNumberSchema('prix', { optional: true }),
    fa2eIncluded: z.boolean().optional(),
    fa2ePrice: createNumberSchema('prix', { optional: true }),
    secondaryOptions: z
      .array(
        z.object({
          optionId: createNumberSchema('id', { isInteger: true }),
          price: createNumberSchema('prix', { nonNegative: true }),
          age: createNumberSchema('âge', { nullish: true, isPositive: true })
        })
      )
      .refine(
        (data) => {
          const optionIds = data.map((option) => option.optionId)
          return new Set(optionIds).size === optionIds.length
        },
        { message: 'Les options ne doivent pas avoir des identifiants dupliqués' }
      )
  })
  .superRefine((data, ctx) => {
    if (data.isMandatoryUnderAge && !data.mandatoryUnderAge)
      ctx.addIssue({
        message: 'Veuillez entrer un âge minimum',
        code: z.ZodIssueCode.custom,
        path: ['mandatoryUnderAge']
      })

    if (data.fa1eIncluded && data.fa1ePrice === undefined)
      ctx.addIssue({
        message: 'Veuillez entrer un prix',
        code: z.ZodIssueCode.custom,
        path: ['fa1ePrice']
      })

    if (data.fa2eIncluded && data.fa2ePrice === undefined)
      ctx.addIssue({
        message: 'Veuillez entrer un prix',
        code: z.ZodIssueCode.custom,
        path: ['fa2ePrice']
      })
  })

const JourneyForm = ({
  productDateId,
  periodName,
  className,
  defaultValues,
  savedOptions,
  ...validationButtonProps
}: Props) => {
  const form = useForm<JourneyInput>({
    resolver: zodResolver(formSchema),
    defaultValues: { ...defaultValues, secondaryOptions: defaultValues?.secondaryOptions ?? [] },
    mode: FORM_MODE
  })

  // State to track which rows have age enabled
  const [ageEnabledRows, setAgeEnabledRows] = useState<Record<number, boolean>>(() => {
    // Initialize based on defaultValues - if age exists, enable the checkbox
    const initial: Record<number, boolean> = {}
    defaultValues?.secondaryOptions?.forEach((option, index) => {
      if (option.age !== undefined) {
        initial[index] = true
      }
    })
    return initial
  })

  const {
    isMandatoryUnderAge,
    mandatoryUnderAge,
    fa1eIncluded,
    fa2eIncluded,
    secondaryOptions,
    fa1ePrice,
    fa2ePrice
  } = form.watch()

  const { data: options } = useQuery({
    queryKey: ['product-date-options', productDateId],
    queryFn: () => getProductDateOptions(productDateId)
  })

  const findOption = useCallback(
    (optionId?: number) =>
      options?.find((option) => option.id === optionId) ??
      savedOptions?.find((option) => option.id === optionId),
    [options, savedOptions]
  )

  const { mutateAsync } = useMutation({
    mutationFn: getOptionProductDatePrice
  })

  const data = useMemo(() => secondaryOptions, [secondaryOptions])

  const columns = useMemo<Column<IdInput<JourneyOptionInput>>[]>(
    () => [
      {
        accessorKey: 'optionId',
        header: 'Option',
        columnHeaderClassName: 'w-1/2',
        cell: ({ row }) => (
          <FormField
            control={form.control}
            name={`secondaryOptions.${row.index}.optionId`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    required
                    {...field}
                    label={'Option'}
                    options={options?.map((option) => ({
                      label: getTranslatedName(option),
                      value: option.id
                    }))}
                    selectedOption={conditionalRender(!!row.original.optionId, {
                      label: getTranslatedName(findOption(row.original.optionId)),
                      value: row.original.optionId
                    })}
                    onChange={async (value) => {
                      field.onChange(value)
                      if (!value) return
                      const optionPrice = await mutateAsync({ optionId: value, productDateId })
                      if (optionPrice)
                        form.setValue(`secondaryOptions.${row.index}.price`, optionPrice.price, {
                          shouldValidate: true
                        })
                      else form.resetField(`secondaryOptions.${row.index}.price`)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )
      },
      {
        accessorKey: 'price',
        columnHeaderClassName: 'w-1/2',
        header: "Prix d'option (€)",
        cell: ({ row }) => (
          <FormField
            control={form.control}
            name={`secondaryOptions.${row.index}.price`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput required {...field} type={'number'} label={'Prix (€)'} />
                </FormControl>
              </FormItem>
            )}
          />
        )
      },
      {
        accessorKey: 'age',
        columnHeaderClassName: 'w-1/3',
        header: 'Âge obligatoire',
        cell: ({ row }) => (
          <div className={'flex flex-col py-3 gap-3'}>
            {!ageEnabledRows[row.index] ? (
              <Checkbox
                checked={false}
                onChange={(value) => {
                  if (value) {
                    setAgeEnabledRows((prev) => ({ ...prev, [row.index]: true }))
                  }
                }}
                label={'Âge requis'}
              />
            ) : (
              <div className={'relative w-24'}>
                <FormField
                  control={form.control}
                  name={`secondaryOptions.${row.index}.age`}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          required
                          {...field}
                          type={'number'}
                          label={'Âge'}
                          errorMessage={fieldState.error?.message}
                          onChange={(e) => {
                            const value = typeof e === 'string' ? e : e?.target?.value
                            field.onChange(value)
                            // If the input is cleared, go back to checkbox
                            if (!value || value === '') {
                              setAgeEnabledRows((prev) => ({ ...prev, [row.index]: false }))
                              form.setValue(`secondaryOptions.${row.index}.age`, null)
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div
                  className="absolute -top-2 -right-2 bg-red rounded-full p-1 cursor-pointer hover:bg-red-active flex items-center justify-center"
                  onClick={() => {
                    setAgeEnabledRows((prev) => ({ ...prev, [row.index]: false }))
                    form.setValue(`secondaryOptions.${row.index}.age`, null)
                  }}
                >
                  <XIcon color={tailwindColors.white.DEFAULT} height="14" width="14" />
                </div>
              </div>
            )}
          </div>
        )
      },
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }) => (
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              const index = row.index
              form.setValue(
                'secondaryOptions',
                secondaryOptions.filter((_, i) => i !== index)
              )
            }}
          />
        )
      }
    ],
    [form, options, findOption, secondaryOptions, ageEnabledRows]
  )

  useEffect(() => {
    if (form.formState.submitCount > 0) {
      if (fa1eIncluded && !fa1ePrice)
        form.setError('fa1ePrice', { message: 'Veuillez entrer un prix' })

      if (fa2eIncluded && !fa2ePrice)
        form.setError('fa2ePrice', { message: 'Veuillez entrer un prix' })

      if (isMandatoryUnderAge && !mandatoryUnderAge)
        form.setError('mandatoryUnderAge', { message: 'Veuillez entrer un âge minimum' })
    }
    return () => {
      form.clearErrors('fa1ePrice')
      form.clearErrors('fa2ePrice')
      form.clearErrors('mandatoryUnderAge')
    }
  }, [
    form.formState.submitCount,
    fa1eIncluded,
    fa1ePrice,
    fa2eIncluded,
    fa2ePrice,
    isMandatoryUnderAge,
    mandatoryUnderAge
  ])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <InnerBox
            title={'Thème principal'}
            className={'border-primary text-black'}
            titleColor={tailwindColors.black.DEFAULT}
          >
            <div className={'flex flex-col gap-3'}>
              <div className={'flex flex-row gap-3'}>
                <FormField
                  control={form.control}
                  name={'mainOptionId'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledSelectInput
                          required
                          {...field}
                          label={'Thème'}
                          options={options?.map((option) => ({
                            label: getTranslatedName(option),
                            value: option.id
                          }))}
                          selectedOption={conditionalRender(!!field.value, {
                            label: getTranslatedName(findOption(field.value)),
                            value: field.value
                          })}
                          onChange={async (value) => {
                            const previousValue = field.value
                            field.onChange(value)
                            if (!value || value === previousValue) return
                            const optionPrice = await mutateAsync({
                              optionId: value,
                              productDateId
                            })
                            if (optionPrice)
                              form.setValue('mainOptionPrice', optionPrice.price, {
                                shouldValidate: true
                              })
                            else form.resetField('mainOptionPrice')
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={'mainOptionPrice'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput
                          required
                          {...field}
                          type={'number'}
                          label={'Prix (€)'}
                          icon={PriceTagIcon}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {options?.length === 0 && (
                <NoteBox
                  note={getNoThemeAvailableMessage(periodName)}
                  color={tailwindColors.red.DEFAULT}
                  className={'bg-red-light'}
                />
              )}

              <Checkbox
                checked={isMandatoryUnderAge}
                onChange={(value) => form.setValue('isMandatoryUnderAge', value)}
                label="Obligatoire en dessous d'un certain âge ?"
              />

              {isMandatoryUnderAge && (
                <FormField
                  control={form.control}
                  name={'mandatoryUnderAge'}
                  render={({ field, fieldState }) => (
                    <FormItem className={'w-1/2'}>
                      <FormControl>
                        <ControlledInput
                          required
                          {...field}
                          type={'number'}
                          label={'Âge'}
                          errorMessage={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>
          </InnerBox>

          <div className={'flex flex-col gap-3'}>
            <Typography variant={'body-medium'}>Option(s) facultative(s)</Typography>
            <DataTable<IdInput<JourneyOptionInput>>
              data={data.map((option, index) => ({ ...option, id: index }))}
              rowCount={data.length}
              overflow
              paginated={false}
              columns={columns}
              disableHover
              displayEmptyState={false}
              footer={
                <Button
                  variant={'secondary'}
                  icon={PlusIcon}
                  label={'Ajouter une option'}
                  type={'button'}
                  className={'border-none'}
                  disabled={secondaryOptions.some((option) => !option.optionId || !option.price)}
                  onClick={() => form.setValue('secondaryOptions', [...secondaryOptions, {}])}
                />
              }
            />

            {form.formState.errors.secondaryOptions?.root && (
              <Typography variant={'small'} color={tailwindColors.red.DEFAULT}>
                {form.formState.errors.secondaryOptions?.root.message}
              </Typography>
            )}
          </div>

          <div className={'flex flex-col gap-3'}>
            <div className={'flex flex-row gap-2 items-center'}>
              <Typography variant={'secondary-body'}>
                Si applicable, préciser les modalités d'accueil
              </Typography>

              <InformationToolTip align={'start'} variant={'warning'}>
                <div className={'flex flex-col gap-2'}>
                  <Typography variant="label-semibold" color={tailwindColors.white.DEFAULT}>
                    &gt; Si au choix du client cocher FA1E et FA2E.
                  </Typography>
                  <Typography variant="label-semibold" color={tailwindColors.white.DEFAULT}>
                    &gt; Si choix imposé au client (ex : FA2E obligatoire à Malte), cocher la case
                    adaptée
                  </Typography>
                  <Typography variant="label-semibold" color={tailwindColors.white.DEFAULT}>
                    &gt; Si séjour en hébergement collectif, ne rien cocher
                  </Typography>
                </div>
              </InformationToolTip>
            </div>

            <div className={'flex items-center gap-6'}>
              <div className="flex gap-3 items-center w-2/6">
                <Checkbox
                  label={'FA1E'}
                  checked={fa1eIncluded}
                  onChange={(value) => {
                    form.setValue('fa1eIncluded', value)
                    form.setValue('fa1ePrice', value ? 0 : undefined)
                  }}
                />
                {fa1eIncluded && (
                  <div className={'w-44'}>
                    <FormField
                      control={form.control}
                      name={'fa1ePrice'}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledInput
                              required
                              {...field}
                              type={'number'}
                              label={'FA1E prix (€)'}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 items-center">
                <Checkbox
                  label={'FA2E'}
                  checked={fa2eIncluded}
                  onChange={(value) => {
                    form.setValue('fa2eIncluded', value)
                    form.setValue('fa2ePrice', value ? 0 : undefined)
                  }}
                />
                {fa2eIncluded && (
                  <div className={'w-44'}>
                    <FormField
                      control={form.control}
                      name={'fa2ePrice'}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledInput
                              required
                              {...field}
                              type={'number'}
                              label={'FA2E prix (€)'}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ValidationButtons hasBorderTop {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default JourneyForm
